/**
 * Write a proposal: a lead, a title, and lines from the catalog (FR-H4). A kit
 * `Dialog` laid out as the Branches dialog is (C14 §10.6 item 7); it saves a
 * **draft** — sending is a separate act, from the list, and it confirms.
 *
 * **Only priced plans can be ticked.** A plan with no price is a plan the
 * service refuses to quote by name (`app/domain/proposals.py: build_lines`),
 * and offering it would be a control whose only outcome is an error message.
 * *Price on request* is shown against it so the firm knows why, rather than
 * wondering where the plan went.
 */

import { Button, Checkbox, Dialog, Input, Select, Skeleton } from '@fiveox/ui'
import { useEffect, useMemo, useState } from 'react'

import { ApiError } from '../../../lib/api'
import { crm, type LeadRecord } from '../../../lib/api/crm'
import { type LineInput, proposals as api } from '../../../lib/api/proposals'
import { services as catalog, priceLabel, type Service } from '../../../lib/api/services'

export function Composer({
  open,
  onClose,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const [leads, setLeads] = useState<LeadRecord[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [leadId, setLeadId] = useState('')
  const [title, setTitle] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** *Save* was pressed — so what is missing is said under its field (C27,
   *  UAT report #13), not only in a tooltip on a greyed-out button. */
  const [attempted, setAttempted] = useState(false)

  useEffect(() => {
    if (!open) return
    setAttempted(false)
    setLeadId('')
    setTitle('')
    setValidUntil('')
    setPicked([])
    setError(null)
    setLoading(true)
    let cancelled = false
    Promise.all([crm.list({ limit: 200 }), catalog.list()])
      .then(([leadBody, catalogBody]) => {
        if (cancelled) return
        // A lead in a terminal stage is not somebody to quote, and a merged one
        // has a survivor. Both are filtered here and refused by the service.
        setLeads(
          leadBody.data.filter(
            (lead) => lead.status === 'active' && lead.stage !== 'won' && lead.stage !== 'lost',
          ),
        )
        setServices(catalogBody.data.services)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Your leads and catalog could not be read.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const plans = useMemo(
    () => services.flatMap((service) => (service.plans ?? []).map((plan) => ({ service, plan }))),
    [services],
  )

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const lines: LineInput[] = picked.map((planId) => ({ plan_id: planId }))
      await api.create({
        lead_id: leadId,
        title: title.trim() || undefined,
        lines,
        valid_until: validUntil || null,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const why = !leadId
    ? 'Choose a lead first.'
    : picked.length === 0
      ? 'Tick at least one plan to quote.'
      : undefined

  return (
    // The kit's dialog (2026-09-25): the panel is bounded by the window and its
    // body scrolls, so a long plan list never pushes the title and the close
    // button off the top.
    <Dialog open={open} onClose={onClose} title="Write a proposal" error={error} width="lg">
      {loading ? (
        <div role="status" aria-busy aria-label="Loading" className="space-y-3">
          <Skeleton className="h-9 w-full rounded-lg" />
          <Skeleton className="h-9 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      ) : (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            if (why) setAttempted(true)
            else void submit()
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="To which lead?"
              required
              aria-required="true"
              value={leadId}
              onChange={(event) => setLeadId(event.target.value)}
              hint={
                leads.length === 0
                  ? 'No lead can be quoted — won, lost and merged leads are left out.'
                  : undefined
              }
              error={attempted && !leadId ? 'Choose a lead.' : null}
            >
              {/* A placeholder for a required answer, so it cannot be picked
                  back once a lead is chosen. */}
              <option value="" disabled hidden>
                Choose a lead…
              </option>
              {leads.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.full_name}
                  {lead.organisation_name ? ` — ${lead.organisation_name}` : ''}
                </option>
              ))}
            </Select>
            <Input
              label="Title"
              value={title}
              maxLength={200}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="GST compliance, 2026-27"
            />
            <div className="sm:col-span-2">
              <fieldset className="space-y-2">
                <legend className="text-foreground mb-1.5 text-sm font-medium">
                  What are you quoting?
                </legend>
                {plans.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Price a plan in your catalog first — a proposal quotes from it.
                  </p>
                ) : (
                  plans.map(({ service, plan }) => (
                    <div
                      key={plan.id}
                      className="border-border flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                    >
                      <Checkbox
                        checked={picked.includes(plan.id)}
                        disabled={!plan.priced}
                        onChange={(event) =>
                          setPicked((current) =>
                            event.target.checked
                              ? [...current, plan.id]
                              : current.filter((id) => id !== plan.id),
                          )
                        }
                        label={`${service.name} — ${plan.name}`}
                      />
                      <span className="text-muted-foreground shrink-0 text-xs">{priceLabel(plan)}</span>
                    </div>
                  ))
                )}
              </fieldset>
            </div>
            <Input
              label="Valid until"
              type="date"
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
              hint="Optional. After this date the acceptance link stops working and says so."
            />
          </div>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {attempted && why && leadId && (
              <p role="status" className="text-destructive self-center text-xs sm:mr-auto">
                {why}
              </p>
            )}
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              Save as a draft
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  )
}
